import { FC, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CopropertyDto } from "api/coproperty";
import { useRiskData } from "../contexts/selectors/selectors";
import { CopropertyIdentityCard } from "./CopropertyIdentityCard";
import { CopropertyRecapPanel } from "./CopropertyRecapPanel";
import { CopropertyStepLayout } from "./CopropertyStepLayout";
import { CopropertySurveyEngine, SurveyStepControls } from "./CopropertySurveyEngine";

export type { SurveyStepControls };

interface CopropertySurveyProps {
  coproperty: CopropertyDto;
  /** Route complète de ce questionnaire (avec riskId) — bouton « Revoir mes réponses » de la sortie. */
  surveyRoute: string;
  /** Appelé quand le questionnaire est enregistré et la copropriété éligible. */
  onCompleted: () => void;
  /** Retour à la sous-étape copropriété (depuis le premier bloc). */
  onExitBack: () => void;
  /** Remonte à la page les libellés et actions des boutons d'étape pour le bloc courant. */
  onControlsChange: (controls: SurveyStepControls) => void;
}

/**
 * Questionnaire COPRO_MRI de l'étape copropriété — l'appréciation du risque rendue
 * bloc par bloc (éléments présents, environnement, habitations, travaux, montants) dans
 * le cadre commun des sous-étapes : rail des blocs, fiche et avancement à gauche, le bloc
 * courant en section de la carte principale, navigation par les boutons d'étape de la
 * page. Le moteur du questionnaire vit dans `CopropertySurveyEngine` (il se rejoue aussi
 * en panneau à la contractualisation) ; cette page lui donne son cadre et la sortie
 * /excluded-coverage quand le serveur prononce le refus.
 */
export const CopropertySurvey: FC<CopropertySurveyProps> = ({
  coproperty,
  surveyRoute,
  onCompleted,
  onExitBack,
  onControlsChange,
}) => {
  const navigate = useNavigate();
  const riskData = useRiskData();
  const savedAnswers = riskData?.type === "MRI" ? riskData.surveyAnswers : undefined;

  const handleRefused = useCallback(
    (reasons: string[]) => {
      navigate("/excluded-coverage", {
        state: {
          message: "Cette copropriété ne peut pas être assurée en MRI",
          reasons,
          coverageType: "MRI",
          secondaryButtonText: "Revoir mes réponses",
          secondaryButtonRoute: surveyRoute,
        },
      });
    },
    [navigate, surveyRoute]
  );

  return (
    <CopropertySurveyEngine
      coproperty={coproperty}
      savedAnswers={savedAnswers}
      onCompleted={onCompleted}
      onRefused={handleRefused}
      onExitBack={onExitBack}
      onControlsChange={onControlsChange}
    >
      {({ rail, message, content }) => (
        <CopropertyStepLayout
          title="Questionnaire copropriété"
          message={message}
          aside={
            <>
              {rail}
              <CopropertyIdentityCard coproperty={coproperty} />
              <CopropertyRecapPanel
                coproperty={coproperty}
                hideIdentity
                checklist={[
                  { label: "Copropriété rattachée", status: "done" },
                  { label: "Questionnaire en cours", status: "active" },
                  { label: "Bâtiments à décrire", status: "todo" },
                ]}
              />
            </>
          }
        >
          {content}
        </CopropertyStepLayout>
      )}
    </CopropertySurveyEngine>
  );
};
